import { useState } from "react";

import { cents, currencyToCents, type GenericForm, getInForm } from "byzantine";
import filters from "byzantine/src/filters";

// generates all valid string paths for an object, including nested objects and arrays
// example:
//   type Row = { description: string; amount: number };
//   type Form = { title: string; payments: Row[] };
//   type Paths = Path<Form>;
//   Paths = "title" | "payments" | "payments.0" | "payments.0.description" | "payments.0.amount"
type Path<T> = T extends object
  ? {
      [K in keyof T & string]: T[K] extends Array<infer U>
        ? `${K}` | `${K}.${number}` | `${K}.${number}.${Path<U>}`
        : T[K] extends object
        ? `${K}` | `${K}.${Path<T[K]>}`
        : `${K}`;
    }[keyof T & string]
  : never;

function createCommonProps<T, Form extends GenericForm<any>>(
  form: Form,
  key: string
) {
  const error = (getInForm(form.touched, key) &&
    getInForm(form.errors, key)) as string;
  const value = getInForm(form.values, key);

  return {
    id: key,
    onBlur: form.handleBlur,
    onChange: form.handleChange,
    value,
    error,
  };
}

export function input<T extends string, Form extends GenericForm<any>>(
  form: Form,
  key: Path<Form["values"]>
) {
  const commonProps = createCommonProps<T, Form>(form, key);
  return commonProps;
}

// to be used with components that have their own onChange handler
// gets the value from the component's onChange, and sets the formik field value
export function fieldWithOnChange<
  T extends string,
  Form extends GenericForm<any>
>(form: Form, key: Path<Form["values"]>) {
  const { error, ...commonProps } = createCommonProps<T, Form>(form, key);
  return {
    ...commonProps,
    onChange: (value: number | string | boolean) => {
      form.setFieldValue(key, value, true);
    },
    error,
  };
}

export function amountInput<T extends string, Form extends GenericForm<any>>(
  form: Form,
  key: Path<Form["values"]>,
  isWholeNumbers = false
) {
  const { error, value, ...rest } = createCommonProps<T, Form>(form, key);
  const currencyOptions = isWholeNumbers
    ? {
        truncate: true,
        hasDecimal: true as any,
      }
    : {};
  const displayValue = filters.currency(cents(value), currencyOptions);
  return {
    ...rest,
    value: displayValue,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      form.setFieldValue(key, currencyToCents(event.target.value), true);
    },
    error,
  };
}

// custom hook for amount input that allows a custom display value when field is not active
export function useAmountInput<T extends string, Form extends GenericForm<any>>(
  form: Form,
  key: Path<Form["values"]>,
  customDisplayValue?: string
) {
  const [active, setActive] = useState(false);

  const { error, value, ...rest } = createCommonProps<T, Form>(form, key);

  const displayValue = filters.currency(cents(value));
  const formattedDisplayValue = customDisplayValue ?? displayValue;

  return {
    ...rest,
    value: active ? displayValue : formattedDisplayValue,
    onFocus: () => setActive(true),
    onBlur: () => setActive(false),
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      form.setFieldValue(key, currencyToCents(event.target.value), true);
    },
    error,
  };
}

/**
 * For use with `<RadioButtons />`
 */
export function radio<
  T extends string,
  Form extends GenericForm<any>,
  K extends keyof Form["values"] & string
>(form: Form, key: K) {
  const commonProps = createCommonProps<T, Form>(form, key);
  return {
    ...commonProps,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      form.setFieldValue(key, event.target.value, true);
    },
  };
}

/**
 * For use with a series of `<Radio />`
 */
export function radioSingle<
  T extends string,
  Form extends GenericForm<any>,
  K extends keyof Form["values"] & string
>(form: Form, key: K, radioValue: Form["values"][K]) {
  const commonProps = createCommonProps<T, Form>(form, key);
  return {
    ...commonProps,
    value: radioValue,
    name: key,
    checked: commonProps.value === radioValue,
    onCheck: (event: Form["values"][K] | string) => {
      form.setFieldValue(key, event, true);
    },
  };
}

export function select<
  T extends string,
  Form extends GenericForm<any>,
  K extends keyof Form["values"] & string
>(form: Form, key: K) {
  const { error, ...commonProps } = createCommonProps<T, Form>(form, key);
  return {
    ...commonProps,
    onChange: (value: string) => form.setFieldValue(key, value, true),
    errorText: error,
    error,
  };
}
